import type { AuthSession } from "@/types/auth"
import { normalizeUserTypes } from "@/constants/user-types"
import { ADMIN_HOME } from "@/constants/paths/admin"
import {
  CONSOLE_SCOPE_PLATFORM,
  CONSOLE_SCOPE_TENANT,
  SELECT_CONSOLE_SCOPE_PATH,
  type ConsoleScope,
} from "@/constants/console-scope"

const PLATFORM_USER_TYPES = new Set([
  "WOEZOR_SUPER",
  "WOEZOR_ADMIN",
  "WOEZOR_SUPPORT",
])

const TENANT_CAPABILITY_TYPES = new Set([
  "WOEZOR_BUSINESS_OWNER",
  "WOEZOR_BUSINESS_ADMIN",
  "WOEZOR_BUSINESS_SERVICE_PROVIDER",
])

export function getEligibleConsoleScopes(
  session: Pick<
    AuthSession,
    | "eligibleConsoleScopes"
    | "businessContexts"
    | "platformContexts"
    | "userType"
  > | null | undefined
): ConsoleScope[] {
  if (session?.eligibleConsoleScopes?.length) {
    return session.eligibleConsoleScopes.filter(
      (s): s is ConsoleScope =>
        s === CONSOLE_SCOPE_PLATFORM || s === CONSOLE_SCOPE_TENANT
    )
  }
  const out = new Set<ConsoleScope>()
  const roles = session?.platformContexts?.roles ?? []
  if (roles.length > 0) out.add(CONSOLE_SCOPE_PLATFORM)
  const ut = normalizeUserTypes(session?.userType ?? undefined)
  if (ut.some((t) => PLATFORM_USER_TYPES.has(t))) {
    out.add(CONSOLE_SCOPE_PLATFORM)
  }
  const bc = session?.businessContexts
  const hasBucket =
    !!bc &&
    (bc.owned?.length > 0 ||
      bc.providerFor?.length > 0 ||
      bc.adminFor?.length > 0)
  if (hasBucket || ut.some((t) => TENANT_CAPABILITY_TYPES.has(t))) {
    out.add(CONSOLE_SCOPE_TENANT)
  }
  return Array.from(out)
}

export function mergeTenantBusinessSummaries(
  profile: AuthSession | null | undefined
): {
  _id?: string
  name?: string
  image?: string
  location?: string
  description?: string
  parentBusiness?: string | null
  industry?: unknown
}[] {
  const map = new Map<
    string,
    {
      _id?: string
      name?: string
      image?: string
      location?: string
      description?: string
      parentBusiness?: string | null
      industry?: unknown
    }
  >()

  const pushList = (
    list:
      | {
          _id?: string
          name?: string
          image?: string
          location?: string
          description?: string
          parentBusiness?: string | { _id?: string } | null
          industry?: unknown
        }[]
      | undefined
  ) => {
    for (const b of list ?? []) {
      if (!b?._id || map.has(b._id)) continue
      const parentId =
        typeof b.parentBusiness === "string"
          ? b.parentBusiness
          : b.parentBusiness?._id
      map.set(b._id, {
        ...b,
        parentBusiness: parentId,
      })
    }
  }

  pushList(profile?.businesses as { _id?: string }[] | undefined)
  pushList(profile?.businessContexts?.owned)
  pushList(profile?.businessContexts?.adminFor)
  pushList(profile?.businessContexts?.providerFor)

  return Array.from(map.values())
}

export type TenantBusinessSummary = ReturnType<
  typeof mergeTenantBusinessSummaries
>[number]

export function getOrganizationKey(
  business: Pick<TenantBusinessSummary, "_id" | "parentBusiness">
): string {
  return business.parentBusiness || business._id || ""
}

export function countTenantOrganizations(
  profile: AuthSession | null | undefined
): number {
  const keys = new Set(
    mergeTenantBusinessSummaries(profile)
      .map(getOrganizationKey)
      .filter(Boolean)
  )
  return keys.size
}

export function hasMultipleTenantBusinesses(session: AuthSession | null): boolean {
  return countTenantOrganizations(session) > 1
}

export function getDefaultActiveBusinessId(
  profile: AuthSession | null | undefined
): string | null {
  const summaries = mergeTenantBusinessSummaries(profile)
  if (!summaries.length) return null

  const lastActive = (profile as Record<string, unknown> | null | undefined)
    ?.lastActiveBusinessId as string | undefined
  if (lastActive && summaries.some((b) => b._id === lastActive)) {
    return lastActive
  }

  const orgKeys = new Set(
    summaries.map(getOrganizationKey).filter(Boolean)
  )
  if (orgKeys.size !== 1) return null

  const root =
    summaries.find((business) => !business.parentBusiness) ?? summaries[0]
  return root?._id ?? null
}

export interface TenantOrganizationGroup {
  orgKey: string
  root: TenantBusinessSummary
  branches: TenantBusinessSummary[]
}

export function groupTenantBusinessesByOrganization(
  profile: AuthSession | null | undefined
): TenantOrganizationGroup[] {
  const summaries = mergeTenantBusinessSummaries(profile)
  const byOrg = new Map<string, TenantBusinessSummary[]>()

  for (const business of summaries) {
    const orgKey = getOrganizationKey(business)
    if (!orgKey) continue
    const list = byOrg.get(orgKey) ?? []
    list.push(business)
    byOrg.set(orgKey, list)
  }

  return Array.from(byOrg.entries()).map(([orgKey, members]) => {
    const root =
      members.find((business) => business._id === orgKey && !business.parentBusiness) ??
      members.find((business) => !business.parentBusiness) ??
      members[0]
    const branches = members.filter((business) => business._id !== root?._id)
    return { orgKey, root, branches }
  })
}

export function resolveLoginNavigatePath(
  session: AuthSession,
  redirectPath: string
): string {
  const eligible = getEligibleConsoleScopes(session)
  const hasPlatform = eligible.includes(CONSOLE_SCOPE_PLATFORM)
  const hasTenant = eligible.includes(CONSOLE_SCOPE_TENANT)
  const pref = session.consoleLandingScope

  const tenantEntry = (): string => {
    if (hasMultipleTenantBusinesses(session)) return "/select-business"
    const dest =
      redirectPath && redirectPath !== "/" ? redirectPath : "/dashboard"
    return dest
  }

  if (hasPlatform && hasTenant) {
    if (pref === CONSOLE_SCOPE_PLATFORM) return ADMIN_HOME
    if (pref === CONSOLE_SCOPE_TENANT) return tenantEntry()
    return SELECT_CONSOLE_SCOPE_PATH
  }

  if (hasPlatform && !hasTenant) return ADMIN_HOME
  if (!hasPlatform && hasTenant) return tenantEntry()

  const fallback =
    redirectPath && redirectPath !== "/" ? redirectPath : "/dashboard"
  return fallback
}

export function effectiveConsoleScope(
  storeScope: ConsoleScope | null,
  user: Pick<AuthSession, "consoleLandingScope"> | null | undefined
): ConsoleScope | null {
  if (
    storeScope === CONSOLE_SCOPE_PLATFORM ||
    storeScope === CONSOLE_SCOPE_TENANT
  ) {
    return storeScope
  }
  if (
    user?.consoleLandingScope === CONSOLE_SCOPE_PLATFORM ||
    user?.consoleLandingScope === CONSOLE_SCOPE_TENANT
  ) {
    return user.consoleLandingScope
  }
  return null
}

export function needsConsoleScopeChooser(
  eligible: ConsoleScope[],
  effectiveScope: ConsoleScope | null
): boolean {
  return (
    eligible.includes(CONSOLE_SCOPE_PLATFORM) &&
    eligible.includes(CONSOLE_SCOPE_TENANT) &&
    effectiveScope === null
  )
}
